import React, { useState, useEffect } from 'react';
import { Package, Link2, X, Check, AlertCircle, Plus, Search, ChevronDown, User, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { cn } from '../../utils/cn';
import { useToast } from '../shared/ToastContainer';

interface OrderBindingInfoProps {
  categoryId: string;
  onUpdate?: () => void;
}

interface OrderBinding {
  order_id: string;
  order_number: string;
  description?: string;
  binding_type: string;
  created_at: string;
  creator_name?: string;
  creator_email?: string;
}

interface ExistingOrder {
  id: string;
  order_number: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

const OrderBindingInfoNew: React.FC<OrderBindingInfoProps> = ({ categoryId, onUpdate }) => {
  const [bindings, setBindings] = useState<OrderBinding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [bindingMode, setBindingMode] = useState<'create' | 'select'>('create');
  const [newOrderNumber, setNewOrderNumber] = useState('');
  const [existingOrders, setExistingOrders] = useState<ExistingOrder[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ orderId: string; orderNumber: string } | null>(null);
  const toast = useToast();

  useEffect(() => { fetchOrderBindings(); }, [categoryId]);

  const fetchOrderBindings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('categories_with_orders')
        .select('order_id, bound_order_number, order_description, binding_type, binding_created_at')
        .eq('id', categoryId)
        .not('order_id', 'is', null);

      if (error) throw error;
      if (!data || data.length === 0) { setBindings([]); return; }

      const { data: ordersWithCreators } = await supabase
        .from('orders')
        .select(`id, created_by, creator:users!created_by(name, email)`)
        .in('id', data.map(item => item.order_id).filter(Boolean));

      const creatorMap = new Map((ordersWithCreators || []).map((o: any) => [o.id, { name: o.creator?.name, email: o.creator?.email }]));

      const formattedBindings: OrderBinding[] = data.map(item => {
        const creator = creatorMap.get(item.order_id);
        return {
          order_id: item.order_id,
          order_number: item.bound_order_number,
          description: item.order_description,
          binding_type: item.binding_type,
          created_at: item.binding_created_at,
          creator_name: creator?.name || null,
          creator_email: creator?.email || null
        };
      });
      setBindings(formattedBindings);
    } catch (error) {
      console.error('获取订单绑定失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingOrders = async () => {
    try {
      setIsLoadingOrders(true);
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_number, description, is_active, created_at')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setExistingOrders(data || []);
    } catch (error) {
      console.error('获取订单列表失败:', error);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (showEditModal && bindingMode === 'select') fetchExistingOrders();
  }, [showEditModal, bindingMode]);

  const handleAddBinding = async () => {
    if (isProcessing) return;
    if (bindingMode === 'create') {
      if (!newOrderNumber.trim()) return;
      return handleCreateAndBindOrder();
    } else {
      if (!selectedOrderId) return;
      return handleBindExistingOrder();
    }
  };

  const handleCreateAndBindOrder = async () => {
    try {
      setIsProcessing(true);
      const { error } = await supabase.rpc('bind_category_to_order', {
        p_category_id: categoryId,
        p_order_number: newOrderNumber.trim(),
        p_binding_type: 'manual'
      });
      if (error) throw error;
      toast.addToast('✅ 订单创建并绑定成功！', 'success', 3000);
      resetModalState();
      fetchOrderBindings();
      onUpdate?.();
    } catch (error) {
      console.error('绑定订单失败:', error);
      toast.addToast('❌ 绑定失败，请检查后重新尝试', 'error', 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBindExistingOrder = async () => {
    try {
      setIsProcessing(true);
      const selectedOrder = existingOrders.find(order => order.id === selectedOrderId);
      if (!selectedOrder) return;
      const { error } = await supabase.rpc('bind_category_to_order', {
        p_category_id: categoryId,
        p_order_number: selectedOrder.order_number,
        p_binding_type: 'manual'
      });
      if (error) throw error;
      toast.addToast('✅ 订单绑定成功！', 'success', 3000);
      resetModalState();
      fetchOrderBindings();
      onUpdate?.();
    } catch (error) {
      console.error('绑定订单失败:', error);
      toast.addToast('❌ 绑定失败，请重新尝试', 'error', 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetModalState = () => {
    setNewOrderNumber('');
    setSelectedOrderId('');
    setOrderSearchTerm('');
    setShowOrderDropdown(false);
    setShowEditModal(false);
    setBindingMode('create');
  };

  const filteredOrders = existingOrders.filter(order => {
    const searchLower = orderSearchTerm.toLowerCase();
    return order.order_number.toLowerCase().includes(searchLower) || (order.description || '').toLowerCase().includes(searchLower);
  });

  const isOrderAlreadyBound = (orderNumber: string) => bindings.some(binding => binding.order_number === orderNumber);

  const handleRemoveBinding = async (orderId: string, orderNumber: string) => {
    setConfirmDelete({ orderId, orderNumber });
  };

  const confirmRemoveBinding = async () => {
    if (!confirmDelete) return;
    try {
      const { error } = await supabase.rpc('unbind_category_from_order', { p_category_id: categoryId, p_order_id: confirmDelete.orderId });
      if (error) throw error;
      toast.addToast('✅ 绑定解除成功！', 'success', 3000);
      fetchOrderBindings();
      onUpdate?.();
    } catch (error) {
      console.error('解除绑定失败:', error);
      toast.addToast('❌ 解除绑定失败，请重试', 'error', 3000);
    } finally {
      setConfirmDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center text-sm text-slate-400">
        <Package className="w-4 h-4 mr-2 animate-pulse" />
        <span>加载订单信息...</span>
      </div>
    );
  }

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('zh-CN');
  const getBindingTypeLabel = (type: string) => {
    switch (type) {
      case 'manual': return '手动绑定';
      case 'auto': return '自动绑定';
      case 'system': return '系统绑定';
      default: return type;
    }
  };

  return (
    <div className="space-y-3">
      {/* 订单绑定显示 - 新设计 */}
      {bindings.length > 0 ? (
        <div className="space-y-2">
          {bindings.map((binding, index) => (
            <div 
              key={`${binding.order_id}-${index}`} 
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-sky-50 to-transparent border border-sky-100 rounded-lg group"
            >
              <div className="p-2 bg-sky-100 rounded-lg text-sky-600">
                <Package size={18} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">
                    订单: {binding.order_number}
                  </span>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-xs font-medium",
                    binding.binding_type === 'manual' ? "bg-sky-100 text-sky-700" :
                    binding.binding_type === 'auto' ? "bg-green-100 text-green-700" :
                    "bg-slate-100 text-slate-600"
                  )}>
                    {getBindingTypeLabel(binding.binding_type)}
                  </span>
                  {binding.creator_name && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      <User size={10} />
                      {binding.creator_name}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {binding.description && (
                    <>
                      <span className="truncate max-w-[200px]">{binding.description}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                    </>
                  )}
                  <span>绑定时间: {formatDate(binding.created_at)}</span>
                </div>
              </div>
              
              <button
                onClick={() => handleRemoveBinding(binding.order_id, binding.order_number)}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                title="解除绑定"
              >
                <X size={16} />
              </button>
            </div>
          ))}
          
          {/* 添加更多绑定按钮 */}
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 hover:bg-sky-50 px-3 py-2 rounded-lg transition-colors"
          >
            <Link2 size={16} />
            绑定其他订单
          </button>
        </div>
      ) : (
        /* 无绑定时的显示 */
        <button
          onClick={() => setShowEditModal(true)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-sky-600 hover:bg-sky-50 px-3 py-2 rounded-lg transition-colors"
        >
          <Package size={16} />
          绑定订单号
        </button>
      )}

      {/* 编辑绑定模态框 - 新设计 */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">绑定订单号</h3>
              <button onClick={resetModalState} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* 绑定模式选择 */}
              <div className="mb-6">
                <div className="flex rounded-xl bg-slate-100 p-1">
                  <button
                    onClick={() => setBindingMode('create')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all',
                      bindingMode === 'create'
                        ? 'bg-white text-sky-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    <Plus size={16} />
                    新建订单
                  </button>
                  <button
                    onClick={() => setBindingMode('select')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all',
                      bindingMode === 'select'
                        ? 'bg-white text-sky-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    <Search size={16} />
                    选择已有订单
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {bindingMode === 'create' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">新订单号</label>
                      <input
                        type="text"
                        value={newOrderNumber}
                        onChange={(e) => setNewOrderNumber(e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                        placeholder="输入新订单号"
                        disabled={isProcessing}
                      />
                    </div>
                    
                    <div className="bg-sky-50 border border-sky-100 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-sky-700">
                          <p className="font-medium mb-1">新建订单模式</p>
                          <ul className="list-disc list-inside space-y-0.5 text-sky-600">
                            <li>如果订单号不存在，系统将自动创建</li>
                            <li>如果订单号已存在且有效，将直接绑定</li>
                            <li>绑定后，用户查询该订单时会自动获得访问权限</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">选择已有订单</label>
                      <div className="relative">
                        <div 
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-white cursor-pointer flex items-center justify-between hover:border-slate-300 transition-colors"
                          onClick={() => setShowOrderDropdown(!showOrderDropdown)}
                        >
                          <span className={selectedOrderId ? "text-slate-800" : "text-slate-400"}>
                            {selectedOrderId ? existingOrders.find(o => o.id === selectedOrderId)?.order_number || '选择订单' : '点击选择订单'}
                          </span>
                          <ChevronDown className={cn('w-5 h-5 text-slate-400 transition-transform', showOrderDropdown && 'rotate-180')} />
                        </div>
                        
                        {showOrderDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden">
                            <div className="p-3 border-b border-slate-100">
                              <input
                                type="text"
                                value={orderSearchTerm}
                                onChange={(e) => setOrderSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                                placeholder="搜索订单号或描述..."
                              />
                            </div>
                            
                            <div className="max-h-48 overflow-y-auto">
                              {isLoadingOrders ? (
                                <div className="p-4 text-center text-slate-400 text-sm">加载中...</div>
                              ) : filteredOrders.length > 0 ? (
                                filteredOrders.map(order => {
                                  const alreadyBound = isOrderAlreadyBound(order.order_number);
                                  return (
                                    <button
                                      key={order.id}
                                      onClick={() => { if (!alreadyBound) { setSelectedOrderId(order.id); setShowOrderDropdown(false); } }}
                                      disabled={alreadyBound}
                                      className={cn(
                                        'w-full text-left px-4 py-3 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-b-0 transition-colors',
                                        selectedOrderId === order.id && 'bg-sky-50',
                                        alreadyBound && 'opacity-50 cursor-not-allowed bg-slate-50'
                                      )}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="font-medium text-slate-800">{order.order_number}</div>
                                          {order.description && <div className="text-xs text-slate-500 mt-0.5">{order.description}</div>}
                                        </div>
                                        {alreadyBound && <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">已绑定</span>}
                                      </div>
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="p-4 text-center text-slate-400 text-sm">{orderSearchTerm ? '未找到匹配的订单' : '暂无可用订单'}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-green-700">
                          <p className="font-medium mb-1">选择已有订单</p>
                          <ul className="list-disc list-inside space-y-0.5 text-green-600">
                            <li>从现有的有效订单中选择一个进行绑定</li>
                            <li>支持按订单号或描述搜索</li>
                            <li>已绑定的订单会显示为灰色不可选</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button onClick={resetModalState} disabled={isProcessing} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">
                  取消
                </button>
                <button
                  onClick={handleAddBinding}
                  disabled={isProcessing || (bindingMode === 'create' ? !newOrderNumber.trim() : !selectedOrderId)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      处理中...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {bindingMode === 'create' ? '创建并绑定' : '绑定订单'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 确认解除绑定对话框 */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">确认解除绑定</h3>
              <p className="text-sm text-slate-500 mb-6">
                确定要解除与订单 <span className="font-medium text-slate-700">{confirmDelete.orderNumber}</span> 的绑定吗？
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={confirmRemoveBinding}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  确认解除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderBindingInfoNew;
